import React from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Card } from 'shadcn/components/ui/Card';
import { cn } from 'shadcn/lib/utils';

import { getPalletLabel } from './palletBuilderUtils';
import { PalletLineItem } from './PalletLineItem';

/**
 * @typedef {{ id: number, code: string, displayName: string }} CompanySource
 *
 * @param {Object} props
 * @param {string} [props.className]
 * @param {CompanySource[]} [props.companySourceOptions]
 * @param {boolean} [props.isThirdPartyAsn]
 * @param {import('../resolveExpirationDefaults').ExpirationDefaults} [props.expirationDefaults]
 * @param {{ id: string, lines: Array<{ id: string, companySourceId: number | null, numCases: number, expiration: string, supplierLot: string }> }} props.pallet
 * @param {number} props.palletIndex
 * @param {() => void} props.onAddLine
 * @param {(lineId: string) => void} props.onRemoveLine
 * @param {(lineId: string, patch: object) => void} props.onUpdateLine
 */
function PalletBuilderCard({
    className,
    companySourceOptions = [],
    expirationDefaults,
    isThirdPartyAsn = false,
    onAddLine,
    onRemoveLine,
    onUpdateLine,
    pallet,
    palletIndex,
}) {
    const totalCases = pallet.lines.reduce((sum, line) => sum + (Number(line.numCases) || 0), 0);
    const casesLabel = totalCases === 1 ? 'case' : 'cases';

    return (
        <Card
            aria-label={`Pallet ${getPalletLabel(palletIndex)}`}
            className={cn('gap-0 border py-0 shadow-none', className)}
        >
            <div className="flex items-center justify-between border-b px-3 py-1.5 text-sm">
                <span className="font-semibold leading-none">Pallet {getPalletLabel(palletIndex)}</span>
                <span className="text-xs text-muted-foreground">
                    {totalCases} {casesLabel}
                </span>
            </div>
            <div className="flex flex-col gap-0 px-2 py-1">
                {pallet.lines.map((line, lineIndex) => (
                    <PalletLineItem
                        key={line.id}
                        canDelete={pallet.lines.length > 1}
                        companySourceOptions={companySourceOptions}
                        expirationDefaults={expirationDefaults}
                        fieldIdPrefix={`pallet-${pallet.id}-line-${line.id}`}
                        isThirdPartyAsn={isThirdPartyAsn}
                        line={line}
                        lineIndex={lineIndex}
                        onChange={(patch) => onUpdateLine(line.id, patch)}
                        onDelete={() => onRemoveLine(line.id)}
                    />
                ))}
            </div>
            <Button
                className="mx-2 mb-2 h-7 w-[calc(100%-1rem)] border border-dashed text-xs"
                type="button"
                variant="outline"
                onClick={onAddLine}
            >
                + Add new line
            </Button>
        </Card>
    );
}

export { PalletBuilderCard };
